import type { APPLICANT_ATTRIBUTION_BOTH_TYPE, LOAN_REQUEST_COMMAND } from 'cpool-constants';
import { AGGREGATE_NAMES, APPLICANT_ATTRIBUTION_BOTH, LOAN_REQUEST_APPLICANT_EDITING_COMMANDS } from 'cpool-constants';
import type { SchemaUserResolver_GetAclPermissions } from 'cpool-tables/backend-resolvers';

export type CanExecuteApplicantCommandParams = {
    applicant: 1 | 2;
    commandName: LOAN_REQUEST_COMMAND;
    permissions: SchemaUserResolver_GetAclPermissions;
};

/** Returns whether the given applicant-editing command is allowed for the specified applicant. */
export function canExecuteApplicantCommand({
    applicant,
    commandName,
    permissions,
}: CanExecuteApplicantCommandParams): boolean {
    const perm = permissions.commands[AGGREGATE_NAMES.LOAN_REQUEST][commandName];
    if (typeof perm === 'boolean') return perm;
    return applicant === 1 ? perm.applicant1 : perm.applicant2;
}

export type CanExecuteForAttributionParams = {
    attribution: APPLICANT_ATTRIBUTION_BOTH_TYPE;
    commandName: LOAN_REQUEST_COMMAND;
    permissions: SchemaUserResolver_GetAclPermissions;
};

/** Resolves permission for a command based on real-estate-style attribution (ANTRAGSTELLER_1, ANTRAGSTELLER_2, BEIDE). */
export function canExecuteForAttribution({
    attribution,
    commandName,
    permissions,
}: CanExecuteForAttributionParams): boolean {
    const perm = permissions.commands[AGGREGATE_NAMES.LOAN_REQUEST][commandName];
    if (typeof perm === 'boolean') return perm;

    switch (attribution) {
        case APPLICANT_ATTRIBUTION_BOTH.ANTRAGSTELLER_1:
            return perm.applicant1;
        case APPLICANT_ATTRIBUTION_BOTH.ANTRAGSTELLER_2:
            return perm.applicant2;
        case APPLICANT_ATTRIBUTION_BOTH.BEIDE:
            return perm.applicant1 && perm.applicant2;
        default:
            return false;
    }
}

/** Returns true if at least one applicant-editing command is allowed for at least one applicant. */
export function canSubmitLeadDetailsForms(permissions: SchemaUserResolver_GetAclPermissions): boolean {
    const lrCommands = permissions.commands[AGGREGATE_NAMES.LOAN_REQUEST];
    return Object.values(LOAN_REQUEST_APPLICANT_EDITING_COMMANDS).some((cmd) => {
        const perm = lrCommands[cmd];
        if (typeof perm === 'boolean') return perm;
        return perm.applicant1 || perm.applicant2;
    });
}
